<template>
  <div v-if="canViewAuthProfile || isStaff" class="mt-4">
    <!-- Header -->
    <div class="glass-card p-3 mb-4 flex items-center justify-between">
      <h2 class="text-lg font-semibold">SNMP Auth Profiles</h2>
      <button
        v-if="canAddAuthProfile || isStaff"
        type="button"
        @click="openAddModal"
        class="px-4 py-2 glass-card bg-blue-600 hover:bg-blue-700 transition-colors duration-150 flex items-center"
        title="Add Auth Profile"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" x2="12" y1="5" y2="19" />
          <line x1="5" x2="19" y1="12" y2="12" />
        </svg>
        Add Auth Profile
      </button>
    </div>

    <p v-if="authProfiles.length === 0" class="text-sm text-gray-500 dark:text-gray-400 mb-4">
      No auth profiles yet. Add one so network devices can reference it.
    </p>

    <div v-else class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
      <div
        v-for="profile in authProfiles"
        :key="profile.id"
        class="glass-card p-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
        @click="openEditModal(profile)"
      >
        <div class="font-medium truncate">{{ profile.name }}</div>
        <div class="text-xs text-gray-500 dark:text-gray-400">
          {{ profile.snmp_version === 'v3' ? 'SNMPv3' : 'SNMPv2c' }}
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <Teleport to="body">
      <div
        v-if="isModalOpen"
        class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40 overflow-y-auto overscroll-contain py-6"
        @click.self="closeModal"
      >
        <div class="glass-card z-50 p-6 w-[90%] max-w-lg max-h-[80vh] relative my-auto flex flex-col focus:outline-none">
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/30 pb-3 mb-4">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-50">
              {{ editingProfileId ? 'Edit' : 'Add' }} SNMP Auth Profile
            </h3>
            <button @click="closeModal" class="bg-transparent hover:bg-transparent text-red-500 hover:text-red-700 dark:hover:text-red-400">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div
            v-if="formError"
            class="mb-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 px-4 py-3 rounded"
          >
            {{ formError }}
          </div>

          <!-- Body -->
          <div class="space-y-4 flex-auto min-h-0 overflow-y-auto p-1 -m-1">
            <div class="flex flex-col">
              <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Name<span class="text-red-500 pl-1">*</span></label>
              <input
                v-model="formData.name"
                type="text"
                @focus="clearFieldError('name')"
                :class="['border rounded px-3 py-1.5 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-white', fieldErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500']"
              />
              <FieldError :message="fieldErrors.name" />
            </div>

            <div class="flex flex-col">
              <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Version<span class="text-red-500 pl-1">*</span></label>
              <GlassSelect v-model="formData.snmp_version" :options="versionOptions" :valueKey="'value'" :labelKey="'label'" />
            </div>

            <!-- SNMPv2c -->
            <div v-if="formData.snmp_version === 'v2c'" class="flex flex-col">
              <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Community String<span class="text-red-500 pl-1">*</span></label>
              <input
                v-model="formData.community_string"
                type="text"
                :placeholder="editingProfileId ? 'Leave blank to keep current' : ''"
                @focus="clearFieldError('community_string')"
                :class="['border rounded px-3 py-1.5 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-white', fieldErrors.community_string ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500']"
              />
              <FieldError :message="fieldErrors.community_string" />
            </div>

            <!-- SNMPv3 -->
            <template v-else>
              <div class="flex flex-col">
                <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Username<span class="text-red-500 pl-1">*</span></label>
                <input
                  v-model="formData.username"
                  type="text"
                  @focus="clearFieldError('username')"
                  :class="['border rounded px-3 py-1.5 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-white', fieldErrors.username ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500']"
                />
                <FieldError :message="fieldErrors.username" />
              </div>
              <div class="flex flex-col">
                <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Security Level<span class="text-red-500 pl-1">*</span></label>
                <GlassSelect v-model="formData.security_level" :options="securityLevelOptions" :valueKey="'value'" :labelKey="'label'" />
                <FieldError :message="fieldErrors.security_level" />
              </div>
              <template v-if="formData.security_level === 'authNoPriv' || formData.security_level === 'authPriv'">
                <div class="flex flex-col">
                  <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Auth Protocol</label>
                  <GlassSelect v-model="formData.auth_protocol" :options="authProtocolOptions" :valueKey="'value'" :labelKey="'label'" />
                </div>
                <div class="flex flex-col">
                  <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Auth Password</label>
                  <input
                    v-model="formData.auth_password"
                    type="password"
                    :placeholder="editingProfileId ? 'Leave blank to keep current' : ''"
                    class="border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                  />
                </div>
              </template>
              <template v-if="formData.security_level === 'authPriv'">
                <div class="flex flex-col">
                  <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Privacy Protocol</label>
                  <GlassSelect v-model="formData.priv_protocol" :options="privProtocolOptions" :valueKey="'value'" :labelKey="'label'" />
                </div>
                <div class="flex flex-col">
                  <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Privacy Password</label>
                  <input
                    v-model="formData.priv_password"
                    type="password"
                    :placeholder="editingProfileId ? 'Leave blank to keep current' : ''"
                    class="border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                  />
                </div>
              </template>
            </template>
          </div>

          <!-- Footer -->
          <div class="flex justify-between pt-4 mt-4 border-t shrink-0">
            <div class="flex gap-2">
              <button
                v-if="editingProfileId ? (canChangeAuthProfile || isStaff) : (canAddAuthProfile || isStaff)"
                type="button"
                @click="saveAuthProfile"
                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              >
                {{ editingProfileId ? 'Save' : 'Add' }}
              </button>
              <button
                type="button"
                @click="closeModal"
                class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
              >
                Cancel
              </button>
            </div>
            <button
              v-if="editingProfileId && (canDeleteAuthProfile || isStaff)"
              type="button"
              @click="showDeleteConfirm = true"
              class="btn bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <DeleteModal
        v-if="showDeleteConfirm"
        :deleting-item="showDeleteConfirm"
        :is-child-object="true"
        :count="[{ name: 'SNMP Auth Profile', value: formData.name }]"
        :componet-objects="[]"
        :is-deleting="isDeleting"
        @delete="confirmDelete"
        @close="showDeleteConfirm = false"
      />
    </Teleport>
  </div>
</template>

<script>
import {
  getAllSnmpAuthProfiles,
  createSnmpAuthProfile,
  updateSnmpAuthProfile,
  deleteSnmpAuthProfile,
} from "@/services/snmpAuthProfileService";
import { useStore } from "@/store";
import GlassSelect from "@/components/GlassSelect.vue";
import FieldError from "@/components/FieldError.vue";
import DeleteModal from "@/components/DeleteModal.vue";

function blankAuthProfileForm() {
  return {
    name: "",
    snmp_version: "v2c",
    community_string: "",
    username: "",
    security_level: "",
    auth_protocol: "",
    auth_password: "",
    priv_protocol: "",
    priv_password: "",
  };
}

export default {
  name: "SnmpAuthProfilesSection",
  components: { GlassSelect, FieldError, DeleteModal },
  props: {
    schemaName: { type: String, default: null },
  },
  data() {
    return {
      authProfiles: [],
      isModalOpen: false,
      showDeleteConfirm: false,
      isDeleting: false,
      editingProfileId: null,
      formData: blankAuthProfileForm(),
      formError: "",
      fieldErrors: {},
      versionOptions: [
        { value: "v2c", label: "SNMPv2c" },
        { value: "v3", label: "SNMPv3" },
      ],
      securityLevelOptions: [
        { value: "noAuthNoPriv", label: "No Auth, No Privacy" },
        { value: "authNoPriv", label: "Auth, No Privacy" },
        { value: "authPriv", label: "Auth with Privacy" },
      ],
      authProtocolOptions: ["MD5", "SHA", "SHA224", "SHA256", "SHA384", "SHA512"].map(
        (protocol) => ({ value: protocol, label: protocol })
      ),
      privProtocolOptions: ["DES", "AES", "AES192", "AES256"].map(
        (protocol) => ({ value: protocol, label: protocol })
      ),
    };
  },
  computed: {
    isStaff() { return useStore.isStaff(); },
    canViewAuthProfile() { return useStore.hasPermission("Can view snmp"); },
    canAddAuthProfile() { return useStore.hasPermission("Can add snmp"); },
    canChangeAuthProfile() { return useStore.hasPermission("Can change snmp"); },
    canDeleteAuthProfile() { return useStore.hasPermission("Can delete snmp"); },
  },
  mounted() {
    this.fetchAuthProfiles();
  },
  methods: {
    async fetchAuthProfiles() {
      try {
        this.authProfiles = await getAllSnmpAuthProfiles(this.schemaName);
      } catch (error) {
        console.error("Error fetching auth profiles:", error);
      }
    },
    openAddModal() {
      this.editingProfileId = null;
      this.formData = blankAuthProfileForm();
      this.formError = "";
      this.fieldErrors = {};
      this.showDeleteConfirm = false;
      this.isModalOpen = true;
    },
    openEditModal(profile) {
      this.editingProfileId = profile.id;
      this.formData = { ...blankAuthProfileForm(), ...profile };
      this.formError = "";
      this.fieldErrors = {};
      this.showDeleteConfirm = false;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    clearFieldError(field) {
      this.formError = "";
      if (this.fieldErrors[field]) delete this.fieldErrors[field];
    },
    async saveAuthProfile() {
      this.formError = "";
      this.fieldErrors = {};
      try {
        const payload = { ...this.formData };
        ["community_string", "auth_password", "priv_password"].forEach((f) => {
          if (!payload[f]) delete payload[f];
        });
        if (this.editingProfileId) {
          await updateSnmpAuthProfile(this.editingProfileId, payload, this.schemaName);
        } else {
          await createSnmpAuthProfile(payload, this.schemaName);
        }
        this.isModalOpen = false;
        await this.fetchAuthProfiles();
      } catch (error) {
        const responseData = error?.response?.data;
        if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
          const generalMessages = [];
          Object.entries(responseData).forEach(([field, value]) => {
            const message = Array.isArray(value) ? value[0] : value;
            if (field in this.formData) {
              this.fieldErrors[field] = message;
            } else {
              generalMessages.push(typeof message === "string" ? message : JSON.stringify(message));
            }
          });
          if (generalMessages.length) this.formError = generalMessages.join(" ");
        } else {
          this.formError = "Something went wrong saving the auth profile.";
        }
        console.error("Error saving auth profile:", error);
      }
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await deleteSnmpAuthProfile(this.editingProfileId, this.schemaName);
        this.showDeleteConfirm = false;
        this.isModalOpen = false;
        await this.fetchAuthProfiles();
      } catch (error) {
        this.showDeleteConfirm = false;
        this.formError = error?.response?.data?.detail
          || "Could not delete this auth profile. It may be in use by a network device.";
        console.error("Error deleting auth profile:", error);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>
